import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { BrowserRouter as Router } from 'react-router-dom'
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'; // Import QueryClient and QueryClientProvider
import { HelmetProvider } from 'react-helmet-async';
import { captureAttribution } from './lib/attribution';
import { initAnalytics } from './lib/analytics';
import { recordTouch } from './lib/touch';
import { captureReferral } from './lib/referral';

// Read the campaign parameters off the landing URL before the router has a chance
// to normalise it away, then bring up whichever trackers consent already allows.
captureAttribution();
// A partner code arrives the same way and has the same one-chance-to-read-it
// problem, so it is captured in the same breath — before the router normalises
// `/r/CODE` or strips `?ref=`.
captureReferral();
initAnalytics();

/**
 * Record the arrival first-party, immediately after capturing it.
 *
 * Here rather than inside a component, for the same reason `captureAttribution`
 * is: a router that normalises the query string, or a redirect on a protected
 * route, can strip the parameters before any component mounts. It writes
 * nothing for an untagged direct visit, so the common case costs one function
 * call and no request.
 */
recordTouch();

// Without these defaults React Query treats every result as immediately stale and
// refetches on each remount and window refocus.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Router>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 2000,
        }}
        reverseOrder={false}
      />
      {/* App supplies ThemeProvider itself; mounting a second one here meant two
          independent theme states writing the same CSS variables. */}
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <App />
        </HelmetProvider>
      </QueryClientProvider>
    </Router>
  </StrictMode>,
)
